/**
 * A collapsible deep-category section: tap-all chips + an inline "add your own" text add.
 * All optional. Shows a count badge when something is selected.
 * @see docs/pennysaver-review-architecture-S73.md §C
 */
import { useState } from 'react';
import { AddYourOwn } from './AddYourOwn.js';

interface ExpandableSectionProps {
  title: string;
  options: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
  onAddCustom: (value: string) => void;
}

export function ExpandableSection({ title, options, selected, onToggle, onAddCustom }: ExpandableSectionProps) {
  const [open, setOpen] = useState(false);

  const customs = selected.filter((s) => !options.includes(s));
  const count = selected.length;

  return (
    <section className={`expandable${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="expandable-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="expandable-title">{title}</span>
        {count > 0 && <span className="expandable-count" aria-label={`${count} selected`}>{count}</span>}
        <span className="expandable-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="expandable-body">
          <div className="chip-row" role="group" aria-label={title}>
            {options.map((opt) => {
              const isOn = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  aria-pressed={isOn}
                  className={`chip${isOn ? ' is-selected' : ''}`}
                  onClick={() => onToggle(opt)}
                >
                  {opt}
                </button>
              );
            })}
            {customs.map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={true}
                className="chip is-selected is-custom"
                onClick={() => onToggle(c)}
              >
                {c}
              </button>
            ))}
          </div>

          <AddYourOwn label={title} onAdd={onAddCustom} />
        </div>
      )}
    </section>
  );
}
